import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Hexagon, User, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("candidate");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await register(name, email, password, role);
      toast.success(`Cuenta creada. Bienvenido, ${user.name}`);
      navigate("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    { value: "candidate", label: "Candidato", desc: "Declaro mi stack de agentes", icon: User },
    { value: "consultant", label: "Consultor", desc: "Evalúo candidatos", icon: Briefcase },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#020617] text-slate-100 p-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <img src="/brand/talenthackers-logo.png" alt="Talent Hackers" className="h-8 w-auto object-contain" />
          <span className="text-[10px] uppercase tracking-[0.2em] text-primary border-l border-slate-700 pl-3">Agentic Deputy</span>
        </div>
        <h1 className="font-heading text-2xl font-bold tracking-tight">Crear cuenta</h1>
        <p className="text-sm text-slate-400 mt-1">Únete a la plataforma de evaluación agentic.</p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {roles.map((r) => (
              <button
                type="button"
                key={r.value}
                data-testid={`role-${r.value}`}
                onClick={() => setRole(r.value)}
                className={`rounded-lg border p-3 text-left transition-colors duration-200 ${
                  role === r.value
                    ? "border-primary bg-primary/10"
                    : "border-slate-800 bg-slate-950 hover:bg-slate-800"
                }`}
              >
                <r.icon size={18} className={role === r.value ? "text-primary" : "text-slate-400"} strokeWidth={1.5} />
                <div className="mt-2 text-sm font-medium text-slate-100">{r.label}</div>
                <div className="text-xs text-slate-500">{r.desc}</div>
              </button>
            ))}
          </div>

          <div>
            <Label htmlFor="name" className="text-slate-300">Nombre completo</Label>
            <Input id="name" data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5 bg-slate-950 border-slate-800 focus:border-primary" />
          </div>
          <div>
            <Label htmlFor="email" className="text-slate-300">Email</Label>
            <Input id="email" type="email" data-testid="register-email-input" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 bg-slate-950 border-slate-800 focus:border-primary" />
          </div>
          <div>
            <Label htmlFor="password" className="text-slate-300">Contraseña</Label>
            <Input id="password" type="password" data-testid="register-password-input" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-1.5 bg-slate-950 border-slate-800 focus:border-primary" />
          </div>
          {error && <p data-testid="register-error" className="text-sm text-red-400">{error}</p>}
          <Button type="submit" disabled={loading} data-testid="register-submit-btn" className="w-full bg-primary text-slate-950 hover:bg-cyan-400">
            {loading ? "Creando..." : "Crear cuenta"}
          </Button>
        </form>

        <p className="mt-6 text-sm text-slate-400">
          ¿Ya tienes cuenta?{" "}
          <Link to="/login" className="text-primary hover:underline" data-testid="go-login-link">Inicia sesión</Link>
        </p>
      </motion.div>
    </div>
  );
}
